import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { getCountryCode } from '../Firebase';

// Import translation files
import enTranslations from './locales/en/translation.json';
import idTranslations from './locales/id/translation.json';
import frTranslations from './locales/fr/translation.json';
import plTranslations from './locales/pl/translation.json';
import elTranslations from './locales/el/translation.json';
import arTranslations from './locales/ar/translation.json';

const resources = {
  en: { translation: enTranslations },
  id: { translation: idTranslations },
  fr: { translation: frTranslations },
  pl: { translation: plTranslations },
  el: { translation: elTranslations },
  ar: { translation: arTranslations },
};

// Initialize i18n synchronously
// initReactI18next will set up the React context provider
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'cookie', 'navigator'],
      caches: ['localStorage', 'cookie'],
      lookupLocalStorage: 'i18nextLng',
      lookupCookie: 'i18next',
    },
    react: {
      useSuspense: true,
    },
  });

// RTL languages - when active, set document direction and lang
const RTL_LANGS = ['ar'];

function applyDirection(lng) {
  const isRTL = RTL_LANGS.includes(lng);
  document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
  document.documentElement.setAttribute('lang', lng);
}

// Apply RTL/LTR on initial load (e.g. if user had Arabic saved)
applyDirection(i18n.language || 'en');

// Handle language changes - save to localStorage and apply RTL when Arabic
i18n.on('languageChanged', (lng) => {
  localStorage.setItem('i18nextLng', lng);
  applyDirection(lng);
});

// Country to language mapping (IQ = Iraq → Arabic with RTL)
const getLanguageForCountry = (country) => {
  const countryLanguageMap = {
    'GR': 'el', // Greece → Greek
    'LB': 'en', // Lebanon → English
    'ID': 'id', // Indonesia → Indonesian
    'IQ': 'ar', // Iraq → Arabic (RTL)
    'PL': 'pl', // Poland → Polish
  };
  return countryLanguageMap[country?.trim().toUpperCase()] || null;
};

// Auto-set language based on country on every page load (if no manual selection)
(async () => {
  try {
    // Check if user has manually selected a language (check both localStorage and sessionStorage)
    const manualSelection = localStorage.getItem('manualLanguageSelected') || sessionStorage.getItem('manualLanguageSelected');
    
    // Only auto-set if no manual selection has been made
    if (!manualSelection) {
      const country = await getCountryCode();
      const targetLanguage = getLanguageForCountry(country);
      const currentLanguage = i18n.language || localStorage.getItem('i18nextLng');
      const lastDetectedCountry = localStorage.getItem('lastDetectedCountry');
      
      // Update language if:
      // 1. Country has a language mapping AND
      // 2. (Current language doesn't match target OR country changed)
      if (targetLanguage) {
        const countryChanged = lastDetectedCountry && lastDetectedCountry !== country;
        const languageMismatch = currentLanguage !== targetLanguage;
        const shouldUpdate = languageMismatch || countryChanged;
        
        if (shouldUpdate) {
          i18n.changeLanguage(targetLanguage);
          applyDirection(targetLanguage);
          localStorage.setItem('i18nextLng', targetLanguage);
          localStorage.setItem('lastDetectedCountry', country);
          console.log(`Auto-set language to ${targetLanguage} for country ${country} (previous: ${currentLanguage}, country changed: ${countryChanged})`);
        } else {
          // Still update lastDetectedCountry even if language doesn't need to change
          localStorage.setItem('lastDetectedCountry', country);
        }
      } else {
        // Store country even if no language mapping (for tracking)
        localStorage.setItem('lastDetectedCountry', country);
      }
    }
  } catch (error) {
    console.warn('Error checking country for language auto-selection:', error);
  }
})();

export default i18n;

